import { addDays, subDays } from 'date-fns'
import { formatDateShort } from '~/shared/helpers'
import {
  DEFAULT_WEEK_DURATION,
  type EpgDate,
  type EpgDateEx,
  type EpgDateString,
} from './index.h'

/**
 * Convert EpgDate to timestamp in ms
 */
export const toTimestamp = (x: EpgDate): number =>
  new Date(x.year, x.month, x.day).getTime()

/**
 * Compare equality of two epg dates
 */
export const isSameDate = (a: EpgDate | Date) => (b: EpgDate | Date) => {
  const x = a instanceof Date ? fromDate(a) : a
  const y = b instanceof Date ? fromDate(b) : b
  return x.year === y.year && x.month === y.month && x.day === y.day
}

/**
 * Checks, if the EpgDate is today,
 * cannot be simply curried `isSameDate(new Date())`, because `new Date()` should be called on every check
 */
export const isToday = (epgDate: EpgDate) => isSameDate(new Date())(epgDate)

/**
 * Clone Date object
 */
export const fromDate = (date: Date): EpgDate => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  return { year, month, day }
}

/**
 * Serialization function, not for UI
 */
export const toString = (x: EpgDate): EpgDateString => {
  const year = String(x.year)
  const month = String(x.month + 1).padStart(2, '0')
  const day = String(x.day).padStart(2, '0')
  return `${year}-${month}-${day}` as EpgDateString
}

const getDateTitle = (
  date: EpgDate,
  {
    isToday,
    isYesterday,
    isTomorrow,
  }: Pick<EpgDateEx, 'isToday' | 'isYesterday' | 'isTomorrow'>
): string => {
  const formattedDate = formatDateShort(
    new Date(date.year, date.month, date.day)
  )

  if (isYesterday) return `Yesterday, ${formattedDate}`
  if (isToday) return `Today, ${formattedDate}`
  if (isTomorrow) return `Tomorrow, ${formattedDate}`

  return formattedDate
}

/**
 * Clone other EpgDate with actualization `isToday`, `isYesterday`, and `isTomorrow` flags
 */
export const fromEpgDate = (x: EpgDate): EpgDateEx => {
  const { year, month, day } = x

  const today = new Date()
  const yesterday = subDays(today, 1)
  const tomorrow = addDays(today, 1)

  const isTodayFlag = isSameDate(today)(x)
  const isYesterdayFlag = isSameDate(yesterday)(x)
  const isTomorrowFlag = isSameDate(tomorrow)(x)

  return {
    year,
    month,
    day,
    isToday: isTodayFlag,
    isYesterday: isYesterdayFlag,
    isTomorrow: isTomorrowFlag,
    title: getDateTitle(x, {
      isToday: isTodayFlag,
      isYesterday: isYesterdayFlag,
      isTomorrow: isTomorrowFlag,
    }),
  }
}

/**
 * Get current date
 */
export const getToday = (): EpgDateEx => {
  return fromEpgDate(fromDate(new Date()))
}

/**
 * Generate week in future from the given date + 1 day before today, yesterday
 */
export const generateWeek = ({
  from,
  duration = DEFAULT_WEEK_DURATION,
  daysBeforeToday = 0,
}: {
  from: EpgDateEx
  duration?: number
  daysBeforeToday?: number
}): EpgDateEx[] => {
  const week = [from]
  const date = new Date(from.year, from.month, from.day)
  const yesterday = new Date(date)
  const daysAfterToday = duration - daysBeforeToday - 1

  // update with days before today
  if (daysBeforeToday) {
    for (let i = 0; i < daysBeforeToday; i++) {
      yesterday.setDate(yesterday.getDate() - 1)
      week.unshift(fromEpgDate(fromDate(yesterday)))
    }
  }

  // update with days after today
  for (let i = 0; i < daysAfterToday; i++) {
    date.setDate(date.getDate() + 1)
    week.push(fromEpgDate(fromDate(date)))
  }

  return week
}
